import React from 'react';
import { SectionType } from 'types/publishedAssets';
import { Section } from 'components/Content/Section';
import { useActiveSectionMinHeight } from 'components/Content/useActiveSectionMinHeight';

interface SectionTabGroupProps {
  sections: SectionType[];
  allSections: SectionType[];
  firstIndex: number;
  navigationMode: 'anchor' | 'tabs';
  isHero: boolean;
  isHidden: boolean;
  hasOwningTab: boolean;
}

/**
 * The sections one tab owns, rendered together.
 *
 * The viewport floor belongs here rather than on a section: a tab's content is its titled section
 * plus every untitled one absorbed after it, and flooring only the titled one leaves a viewport of
 * empty space between it and its own siblings (ENG-4526). Around the whole group the floor does
 * what it was written for — a short tab still reaches far enough down the page for the tab bar to
 * pin — without pushing anything apart.
 *
 * The hero renders bare, with no element around it. It carries neither the floor nor the hidden
 * flag, and the sticky tab bar sits inside it: `position: sticky` is confined to the bar's parent,
 * so any box drawn around the hero shortens the bar's travel to the hero's own height and it
 * scrolls out of view instead of staying pinned down the page.
 */
export function SectionTabGroup(props: SectionTabGroupProps) {
  const { sections, allSections, firstIndex, navigationMode, isHero, isHidden, hasOwningTab } = props;
  const minHeight = useActiveSectionMinHeight({
    enabled: navigationMode === 'tabs' && !isHero && hasOwningTab,
  });

  const members = sections.map((section, offset) => (
    <Section
      key={section.id}
      section={section}
      index={firstIndex + offset}
      sections={allSections}
      navigationMode={navigationMode}
    />
  ));

  // Nothing ever merges into the hero's group, so it holds exactly one section to hand back bare.
  if (isHero) {
    return members[0] ?? null;
  }

  return (
    <div style={minHeight ? { minHeight } : undefined} hidden={isHidden}>
      {members}
    </div>
  );
}
